import React, { useEffect, useRef } from "react";
import { Player } from "@lottiefiles/react-lottie-player";
import contactAnimation from "../assests/lottie/contact.json";
import { FiMail, FiUser, FiMessageCircle, FiPhone } from "react-icons/fi";
import WhatsAppButton from "../components/WhatsAppButton";
import AOS from "aos";
import "aos/dist/aos.css";
import emailjs from '@emailjs/browser';
import { Helmet } from 'react-helmet';

const Contact = () => {
  const form = useRef();

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  }, []);
  const sendEmail = (e) => {
    e.preventDefault();
  
    // First mail to Admin
    emailjs.sendForm(
      'service_yqdrhgg',
      'template_9jjo48s',
      form.current,
      'raKwH9UAAQlOTIlPP'
    )
    .then((result) => {
      console.log("Admin mail sent!");
  
      // Now send second mail to user
      emailjs.sendForm(
        'service_yqdrhgg',
        'template_ogohxwv', // <- Your second template
        form.current,
        'raKwH9UAAQlOTIlPP'
      )
      .then((result) => {
        alert("Message sent to both Admin and User!");
        form.current.reset();
      })
      .catch((error) => {
        alert("Admin mail sent but failed to send user copy.");
      });
  
    })
    .catch((error) => {
      alert("Failed to send message. Please try again.");
    });
  };
  
  return (
    <>
      <Helmet>
        <title>Contact Us | Kavipriya Associates - Get Expert Tax & Compliance Consultation</title>
        <meta
          name="description"
          content="Contact Kavipriya Associates for expert tax, GST, DGFT, customs, and compliance services. Get free consultation for your business needs. Call us today!"
        />
        <meta
          name="keywords"
          content="Contact Kavipriya Associates, Tax Consultant Contact, GST Services Contact, DGFT Services, Customs Clearance Contact, Financial Services Consultation, Coimbatore Tax Expert"
        />
        <meta name="author" content="Kavipriya Associates" />
        <link rel="canonical" href="https://kavipriyaassociates.com/contact" />

        {/* Open Graph / Facebook */}
        <meta property="og:title" content="Contact Kavipriya Associates - Expert Tax & Compliance Services" />
        <meta
          property="og:description"
          content="Get expert consultation for tax, GST, DGFT, customs, and compliance services. Free consultation available. Contact us today!"
        />
        <meta property="og:image" content="https://kavipriyaassociates.com/Logo.png" />
        <meta property="og:url" content="https://kavipriyaassociates.com/contact" />
        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta name="twitter:title" content="Contact Kavipriya Associates - Get Expert Consultation" />
        <meta
          name="twitter:description"
          content="Expert tax, GST, DGFT, and compliance services consultation. Free consultation available. Contact us today!"
        />
        <meta name="twitter:image" content="https://kavipriyaassociates.com/Logo.png" />
        <meta name="twitter:card" content="summary_large_image" />

        <script type="application/ld+json">
          {`
    {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contact Kavipriya Associates",
      "description": "Contact us for expert tax, GST, DGFT, customs, and compliance services",
      "url": "https://kavipriyaassociates.com/contact",
      "mainEntity": {
        "@type": "Organization",
        "name": "Kavipriya Associates",
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+91-9876543210",
          "contactType": "customer service",
          "areaServed": "IN",
          "availableLanguage": "English"
        },
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Coimbatore",
          "addressRegion": "Tamil Nadu",
          "addressCountry": "India"
        }
      }
    }
    `}
        </script>
      </Helmet>

      <div className="relative min-h-screen bg-white overflow-x-hidden">
        {/* Top SVG Wave Hero */}
        <div className="relative w-full bg-gradient-to-br from-blue-100 via-purple-100 to-white pb-16 sm:pb-20">
          <div className="max-w-2xl mx-auto text-center pt-12 sm:pt-16 pb-8 sm:pb-10 relative z-10 px-4">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900 mb-3 sm:mb-4">Contact Us</h1>
            <p className="text-base sm:text-lg text-gray-600 mb-2">We'd love to hear from you! Reach out for any queries, feedback, or just to say hello.</p>
          </div>
          {/* Decorative Animated Dots */}
          <span className="hidden sm:block absolute top-10 left-10 w-3 h-3 bg-purple-200 rounded-full animate-pulse" />
          <span className="hidden sm:block absolute top-24 right-24 w-2 h-2 bg-blue-200 rounded-full animate-bounce" />
          <span className="hidden sm:block absolute bottom-10 left-1/3 w-2 h-2 bg-purple-300 rounded-full animate-ping" />
          {/* SVG Wave */}
          <svg className="absolute bottom-0 left-0 w-full h-120 sm:h-24" viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill="#fff" d="M0,40 C480,120 960,0 1440,60 L1440,120 L0,120 Z" />
          </svg>
        </div>

        {/* Main Section: Form + Animation */}
        <section className="relative z-10 max-w-4xl mx-auto -mt-10 sm:-mt-24 px-2 sm:px-4 flex flex-col md:flex-row items-center gap-8 md:gap-16">
          {/* SVG Animation */}
          <div className="w-full md:w-1/2 flex justify-center mb-6 md:mb-0">
            <Player
              autoplay
              loop
              src={contactAnimation}
              className="w-[180px] xs:w-[200px] sm:w-[220px] md:w-[260px] lg:w-[320px] h-auto drop-shadow-xl"
            />
          </div>
          {/* Contact Form Card */}
          <div className="w-full md:w-1/2">
            <form ref={form} onSubmit={sendEmail} className="bg-white border border-blue-100 rounded-2xl shadow-xl p-4 xs:p-5 sm:p-6 md:p-8 space-y-5 sm:space-y-6">
              <h2 className="text-xl sm:text-2xl font-bold text-blue-700 text-center mb-2">Get in Touch</h2>
              {/* Name */}
              <div className="relative">
                <label className="block text-gray-700 font-semibold mb-1 text-sm sm:text-base">Name</label>
                <FiUser className="absolute top-10 left-3 text-blue-400" />
                <input
                  type="text"
                  name="user_name"
                  pattern="^[A-Za-z ]{2,}$"
                  title="Name should contain only letters and spaces, at least 2 characters."
                  className="w-full pl-10 pr-4 py-2 sm:py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white shadow-sm text-sm sm:text-base"
                  placeholder="Your Name"
                  required
                />
              </div>
              {/* Email */}
              <div className="relative">
                <label className="block text-gray-700 font-semibold mb-1 text-sm sm:text-base">Email</label>
                <FiMail className="absolute top-10 left-3 text-blue-400" />
                <input
                  type="email"
                  name="user_email"
                  pattern="^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$"
                  title="Please enter a valid email address."
                  className="w-full pl-10 pr-4 py-2 sm:py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white shadow-sm text-sm sm:text-base"
                  placeholder="Your Email"
                  required
                />
              </div>
              {/* Phone */}
              <div className="relative">
                <label className="block text-gray-700 font-semibold mb-1 text-sm sm:text-base">Mobile Number</label>
                <FiPhone className="absolute top-10 left-3 text-blue-400" />
                <input
                  type="text"
                  name="user_phone"
                  pattern="^[6-9]\d{9}$"
                  title="Please enter a valid 10-digit Indian mobile number."
                  className="w-full pl-10 pr-4 py-2 sm:py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white shadow-sm text-sm sm:text-base"
                  placeholder="Your Mobile Number"
                  required
                />
              </div>
              {/* Message */}
              <div className="relative">
                <label className="block text-gray-700 font-semibold mb-1 text-sm sm:text-base">Message</label>
                <FiMessageCircle className="absolute top-10 left-3 text-blue-400" />
                <textarea
                  name="message"
                  className="w-full pl-10 pr-4 py-2 sm:py-3 h-24 sm:h-28 resize-none border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white shadow-sm text-sm sm:text-base"
                  placeholder="Your Message"
                  required
                />
              </div>
              {/* Submit */}
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 sm:px-6 sm:py-3 rounded-lg font-semibold shadow-lg hover:scale-105 hover:shadow-xl transition-transform duration-300 text-sm sm:text-base"
              >
                🚀 Send Message
              </button>
            </form>
          </div>
        </section>

        {/* Optional Bottom SVG Wave Divider */}
        {/* Optional Bottom SVG Wave Divider */}
        <div className="w-full mt-16 sm:mt-24">
          <svg className="hidden sm:block w-full h-16" viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill="#f3f4f6" d="M0,40 C480,120 960,0 1440,60 L1440,80 L0,80 Z" />
          </svg>
        </div>
        {/* Floating WhatsApp Button */}
        <WhatsAppButton />
      </div>
    </>
  );
};

export default Contact;
